import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { EyeIcon, EyeOffIcon, MailIcon, LockIcon, UserIcon, AlertCircleIcon } from 'lucide-react';
export const SignupPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const {
    signup,
    loginWithGoogle,
    authError
  } = useAuth();
  const navigate = useNavigate();
  // Use the global auth error if it exists
  useEffect(() => {
    if (authError) {
      setError(authError);
    }
  }, [authError]);
  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirmPassword) {
      return setError('Passwords do not match');
    }
    if (password.length < 6) {
      return setError('Password must be at least 6 characters');
    }
    try {
      setError('');
      setLoading(true);
      await signup(email, password, name);
      navigate('/dashboard');
    } catch (err: any) {
      if (err.code === 'auth/email-already-in-use') {
        setError('Email is already in use');
      } else {
        setError('Failed to create an account');
        console.error(err);
      }
    } finally {
      setLoading(false);
    }
  }
  async function handleGoogleSignUp() {
    try {
      setError('');
      setLoading(true);
      console.log('Initiating Google sign-up...');
      await loginWithGoogle();
      console.log('Google sign-up successful, redirecting...');
      // Check if we have a current user before redirecting
      // This helps ensure auth state has properly updated
      setTimeout(() => {
        navigate('/dashboard');
        setLoading(false);
      }, 1000); // Small delay to ensure auth state has updated
    } catch (err: any) {
      console.error('Google sign-up failed:', err);
      // Error is now handled in AuthContext and displayed via authError
      setLoading(false);
    }
  }
  return <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8" data-id="element-540">
      <div className="max-w-md w-full space-y-8 bg-white p-8 rounded-lg shadow-md" data-id="element-541">
        <div data-id="element-542">
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900" data-id="element-543">
            Create your reporter account
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600" data-id="element-544">
            Join our team of journalists
          </p>
        </div>
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative flex items-start" role="alert" data-id="element-545">
            <AlertCircleIcon className="h-5 w-5 mr-2 mt-0.5 flex-shrink-0" data-id="element-546" />
            <span className="block" data-id="element-547">{error}</span>
          </div>}
        <form className="mt-8 space-y-6" onSubmit={handleSubmit} data-id="element-548">
          <div className="rounded-md shadow-sm -space-y-px" data-id="element-549">
            <div className="relative" data-id="element-550">
              <label htmlFor="name" className="sr-only" data-id="element-551">
                Full Name
              </label>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-552">
                <UserIcon className="h-5 w-5 text-gray-400" data-id="element-553" />
              </div>
              <input id="name" name="name" type="text" required value={name} onChange={e => setName(e.target.value)} className="appearance-none rounded-none relative block w-full px-3 py-3 pl-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm" placeholder="Full Name" data-id="element-554" />
            </div>
            <div className="relative" data-id="element-555">
              <label htmlFor="email-address" className="sr-only" data-id="element-556">
                Email address
              </label>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-557">
                <MailIcon className="h-5 w-5 text-gray-400" data-id="element-558" />
              </div>
              <input id="email-address" name="email" type="email" autoComplete="email" required value={email} onChange={e => setEmail(e.target.value)} className="appearance-none rounded-none relative block w-full px-3 py-3 pl-10 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm" placeholder="Email address" data-id="element-559" />
            </div>
            <div className="relative" data-id="element-560">
              <label htmlFor="password" className="sr-only" data-id="element-561">
                Password
              </label>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-562">
                <LockIcon className="h-5 w-5 text-gray-400" data-id="element-563" />
              </div>
              <input id="password" name="password" type={showPassword ? 'text' : 'password'} required value={password} onChange={e => setPassword(e.target.value)} className="appearance-none rounded-none relative block w-full px-3 py-3 pl-10 pr-10 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm" placeholder="Password" data-id="element-564" />
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center" data-id="element-565">
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="text-gray-400 hover:text-gray-500 focus:outline-none" data-id="element-566">
                  {showPassword ? <EyeOffIcon className="h-5 w-5" data-id="element-567" /> : <EyeIcon className="h-5 w-5" data-id="element-568" />}
                </button>
              </div>
            </div>
            <div className="relative" data-id="element-569">
              <label htmlFor="confirm-password" className="sr-only" data-id="element-570">
                Confirm Password
              </label>
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-571">
                <LockIcon className="h-5 w-5 text-gray-400" data-id="element-572" />
              </div>
              <input id="confirm-password" name="confirm-password" type={showPassword ? 'text' : 'password'} required value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} className="appearance-none rounded-none relative block w-full px-3 py-3 pl-10 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 focus:z-10 sm:text-sm" placeholder="Confirm Password" data-id="element-573" />
            </div>
          </div>
          <div data-id="element-574">
            <button type="submit" disabled={loading} className={`group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-md text-white ${loading ? 'bg-blue-400' : 'bg-blue-600 hover:bg-blue-700'} focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500`} data-id="element-575">
              {loading ? <span className="absolute left-0 inset-y-0 flex items-center pl-3" data-id="element-576">
                  <div className="h-5 w-5 border-t-2 border-b-2 border-white rounded-full animate-spin" data-id="element-577"></div>
                </span> : <span className="absolute left-0 inset-y-0 flex items-center pl-3" data-id="element-578">
                  <UserIcon className="h-5 w-5 text-blue-500 group-hover:text-blue-400" aria-hidden="true" data-id="element-579" />
                </span>}
              {loading ? 'Creating account...' : 'Sign up'}
            </button>
          </div>
        </form>
        <div className="mt-6" data-id="element-580">
          <div className="relative" data-id="element-581">
            <div className="absolute inset-0 flex items-center" data-id="element-582">
              <div className="w-full border-t border-gray-300" data-id="element-583"></div>
            </div>
            <div className="relative flex justify-center text-sm" data-id="element-584">
              <span className="px-2 bg-white text-gray-500" data-id="element-585">
                Or continue with
              </span>
            </div>
          </div>
          <div className="mt-6" data-id="element-586">
            <button onClick={handleGoogleSignUp} disabled={loading} className="w-full flex justify-center py-2 px-4 border border-gray-300 rounded-md shadow-sm bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500" data-id="element-587">
              <svg className="h-5 w-5 mr-2" viewBox="0 0 24 24" data-id="element-588">
                <g transform="matrix(1, 0, 0, 1, 27.009001, -39.238998)" data-id="element-589">
                  <path fill="#4285F4" d="M -3.264 51.509 C -3.264 50.719 -3.334 49.969 -3.454 49.239 L -14.754 49.239 L -14.754 53.749 L -8.284 53.749 C -8.574 55.229 -9.424 56.479 -10.684 57.329 L -10.684 60.329 L -6.824 60.329 C -4.564 58.239 -3.264 55.159 -3.264 51.509 Z" data-id="element-590" />
                  <path fill="#34A853" d="M -14.754 63.239 C -11.514 63.239 -8.804 62.159 -6.824 60.329 L -10.684 57.329 C -11.764 58.049 -13.134 58.489 -14.754 58.489 C -17.884 58.489 -20.534 56.379 -21.484 53.529 L -25.464 53.529 L -25.464 56.619 C -23.494 60.539 -19.444 63.239 -14.754 63.239 Z" data-id="element-591" />
                  <path fill="#FBBC05" d="M -21.484 53.529 C -21.734 52.809 -21.864 52.039 -21.864 51.239 C -21.864 50.439 -21.724 49.669 -21.484 48.949 L -21.484 45.859 L -25.464 45.859 C -26.284 47.479 -26.754 49.299 -26.754 51.239 C -26.754 53.179 -26.284 54.999 -25.464 56.619 L -21.484 53.529 Z" data-id="element-592" />
                  <path fill="#EA4335" d="M -14.754 43.989 C -12.984 43.989 -11.404 44.599 -10.154 45.789 L -6.734 42.369 C -8.804 40.429 -11.514 39.239 -14.754 39.239 C -19.444 39.239 -23.494 41.939 -25.464 45.859 L -21.484 48.949 C -20.534 46.099 -17.884 43.989 -14.754 43.989 Z" data-id="element-593" />
                </g>
              </svg>
              Sign up with Google
            </button>
          </div>
        </div>
        <div className="text-center mt-4" data-id="element-594">
          <p className="text-sm text-gray-600" data-id="element-595">
            Already have an account?{' '}
            <Link to="/login" className="font-medium text-blue-600 hover:text-blue-500" data-id="element-596">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>;
};